import { Fragment, useState } from 'react';
import { api, type ReferralDetail as Detail, type Role, type SignupInspection } from '../api';
import { Banner, Card, Loading, Modal, Status, useAsync, when } from '../ui';

/**
 * One referral, in full.
 *
 * The question an operator is answering here is always the same: why is this
 * in the state it is in, and what happens next. So the check results and the
 * audit timeline are the page — not an afterthought behind a tab.
 */
export function ReferralDetail({ id, role, back, openDriver }: {
  id: string; role: Role; back: () => void; openDriver: (driverRef: string) => void;
}) {
  const { data, error, loading, reload } = useAsync<Detail>(() => api.referral(id), [id]);
  const [releasing, setReleasing] = useState(false);
  const [result, setResult] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null);
  const [confirm, setConfirm] = useState(false);

  if (loading) return <Loading label="Loading referral" />;
  if (error) return <Banner tone="danger" title="Could not load this referral">{error}</Banner>;
  if (!data) return null;

  const isRetry = data.status === 'PAYOUT_FAILED';
  const canRelease = (role === 'zeeh_admin' || role === 'indrive_admin') && (data.status === 'APPROVED' || isRetry);

  async function release() {
    setConfirm(false);
    setReleasing(true);
    try {
      const r = await api.releasePayout(id);
      setResult(
        r.status === 'paid' ? { tone: 'ok', text: 'Payout succeeded. The float has been debited.' }
        : r.status === 'deferred' ? { tone: 'warn', text: `Payout deferred: ${r.reasonCodes.join(', ')}. The referral stays approved and will retry.` }
        : r.status === 'pending_reconciliation' ? { tone: 'warn', text: 'The provider gave no definite outcome. The hold is retained and reconciliation will settle it — nothing will be retried in the meantime.' }
        : { tone: 'danger', text: `Payout failed: ${r.reasonCodes.join(', ')}.` },
      );
      reload();
    } catch (e) {
      setResult({ tone: 'danger', text: (e as Error).message });
    } finally {
      setReleasing(false);
    }
  }

  return (
    <div className="stack">
      <div className="row">
        <button className="btn ghost sm" onClick={back}>← All referrals</button>
        <div className="spacer" style={{ flex: 1 }} />
        {canRelease && (
          <button className="btn primary" disabled={releasing} onClick={() => setConfirm(true)}>
            {releasing ? <><span className="spin" /> {isRetry ? 'Retrying' : 'Releasing'}</> : isRetry ? 'Retry payout' : 'Release payout'}
          </button>
        )}
      </div>

      {result && <Banner tone={result.tone}>{result.text}</Banner>}

      {data.driver_kyc.awaiting_driver && <DriverKycCard id={id} kyc={data.driver_kyc} role={role} cancellable={data.status === 'KYC_PENDING'} indriveName={data.indrive_name} onChanged={reload} />}

      {data.status === 'FUNDS_HELD' && (
        <Banner tone="warn" title="Funds held">
          The destination account was rejected by the bank. No money left the float — the hold was released.
          A corrected account must be supplied before this can be retried.
        </Banner>
      )}
      {(data.referrer_risk.shares_identity_with > 0 || data.referrer_risk.shares_account_with > 0) && (
        <Banner
          tone={data.referrer_risk.shares_identity_with > 0 ? 'danger' : 'warn'}
          title="This referrer is connected to other driver accounts"
          action={
            <button className="btn sm" onClick={() => openDriver(data.referring_driver_id!)}>
              Open driver
            </button>
          }
        >
          {[
            data.referrer_risk.shares_identity_with > 0 &&
              `shares an identity with ${data.referrer_risk.shares_identity_with} other account${data.referrer_risk.shares_identity_with === 1 ? '' : 's'}`,
            data.referrer_risk.shares_account_with > 0 &&
              `shares a payout account with ${data.referrer_risk.shares_account_with}`,
            data.referrer_risk.shares_device_with > 0 &&
              `shares a device with ${data.referrer_risk.shares_device_with}`,
          ].filter(Boolean).join(', ')}
          . Fraud checks ran when this referral arrived; these connections may have formed since.
        </Banner>
      )}

      {data.payouts.some((p) => p.status === 'pending_reconciliation') && (
        <Banner tone="warn" title="Awaiting reconciliation">
          A payout attempt returned no definite outcome. The hold is still in place and nothing will be retried
          until the provider confirms what happened.
        </Banner>
      )}

      <div className="detail-grid">
        <div className="stack">
          <Card title="Referral">
            <dl className="dl">
              <dt>Reference</dt><dd className="mono">{data.referral_id}</dd>
              <dt>Status</dt><dd><Status value={data.status} /></dd>
              <dt>Commission</dt><dd style={{ fontWeight: 600 }}>{data.amount_display}</dd>
              <dt>Referring driver</dt>
              <dd>
                {data.referring_driver_id ? (
                  <button
                    className="btn ghost sm"
                    style={{ padding: 0, height: 'auto', fontWeight: 550 }}
                    onClick={() => openDriver(data.referring_driver_id!)}
                  >
                    {data.referring_driver_id} →
                  </button>
                ) : '—'}
              </dd>
              <dt>Name from inDrive</dt>
              <dd>{data.indrive_name ?? <span className="muted">not on file</span>}</dd>
              <dt>Referred driver</dt>
              <dd>
                {data.referred_driver_id ? (
                  <button
                    className="btn ghost sm"
                    style={{ padding: 0, height: 'auto', fontWeight: 550 }}
                    onClick={() => openDriver(data.referred_driver_id!)}
                  >
                    {data.referred_driver_id} →
                  </button>
                ) : '—'}
              </dd>
              <dt>Destination</dt>
              <dd>
                <span className="mono">{data.destination_masked}</span>{' '}
                <span className="muted small">
                  {data.payout_channel === 'bank' ? `bank ${data.bank_code ?? ''}` : data.wallet_provider}
                </span>
              </dd>
              <dt>Source</dt><dd>{data.source}{data.batch_id ? ` · batch ${data.batch_id.slice(0, 8)}` : ''}</dd>
              {data.external_ref && <><dt>inDrive ref</dt><dd className="mono">{data.external_ref}</dd></>}
              <dt>Received</dt><dd>{when(data.created_at)}</dd>
              <dt>KYC window</dt><dd>{when(data.expires_at)}</dd>
            </dl>

            {data.reasons_explained.length > 0 && (
              <>
                <div className="sep" style={{ margin: '16px 0 12px' }} />
                <div className="label small muted" style={{ marginBottom: 8, fontWeight: 600 }}>Reason codes</div>
                {data.reasons_explained.map((r) => (
                  <div key={r.code} style={{ marginBottom: 6 }}>
                    <span className="code-chip">{r.code}</span>
                    <span className="small muted">{r.label}</span>
                  </div>
                ))}
              </>
            )}
          </Card>

          <Card title="Checks" flush>
            {!data.checks.kyc && !data.checks.fraud ? (
              <div style={{ padding: 18 }} className="muted small">No checks have run yet.</div>
            ) : (
              <>
                {data.checks.kyc && (
                  <>
                    <SectionLabel>Identity & compliance · {data.checks.kyc.decision}</SectionLabel>
                    <Check name="BVN resolved" state={data.checks.kyc.bvn_resolved} />
                    <Check name="NIN resolved" state={data.checks.kyc.nin_resolved} />
                    <Check name="Payout account resolved at bank" state={data.checks.kyc.account_resolved} />
                    <Check
                      name="Liveness & face match"
                      state={data.checks.kyc.liveness ? data.checks.kyc.liveness.is_live : null}
                      note={data.checks.kyc.liveness ? `score ${data.checks.kyc.liveness.match_score.toFixed(2)}` : 'no selfie collected'}
                    />
                    <Check name="Sanctions screening" state={invert(data.checks.kyc.sanctions_hit)} note={data.checks.kyc.sanctions_hit ? 'match found' : undefined} />
                    <Check name="Terrorist watchlist" state={invert(data.checks.kyc.terrorist_hit)} note={data.checks.kyc.terrorist_hit ? 'match found' : undefined} />
                    <Check name="Politically exposed person" state={invert(data.checks.kyc.pep_hit)} note={data.checks.kyc.pep_hit ? 'requires enhanced diligence' : undefined} />
                    <Check
                      name="AML risk score"
                      state={data.checks.kyc.aml_score === null ? null : data.checks.kyc.aml_score < 70}
                      note={data.checks.kyc.aml_score === null ? undefined : `${data.checks.kyc.aml_score} / 100`}
                    />
                  </>
                )}
                {data.checks.fraud && (
                  <>
                    <SectionLabel>Fraud · {data.checks.fraud.decision} · score {data.checks.fraud.score}</SectionLabel>
                    <Check name="Not a self-referral" state={!data.checks.fraud.flags.self_referral} />
                    <Check name="Device not shared" state={!data.checks.fraud.flags.device} />
                    <Check name="Referral rate within limit" state={!data.checks.fraud.flags.velocity} />
                    <Check name="Payout account not reused" state={!data.checks.fraud.flags.account_reuse} />
                  </>
                )}
              </>
            )}
          </Card>

          {data.payouts.length > 0 && (
            <Card title="Payout attempts" flush>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>#</th><th>Status</th><th className="num">Amount</th>
                      <th>Provider</th><th>Reference</th><th className="num">Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.payouts.map((p) => (
                      <Fragment key={p.payout_id}>
                      <tr>
                        <td className="num">{p.attempt_no}</td>
                        <td><span className={`pill ${p.status === 'success' ? 'ok' : p.status === 'failed' ? 'danger' : 'warn'}`}>{p.status.replace(/_/g, ' ')}</span></td>
                        <td className="num">{p.amount_display}</td>
                        <td className="small">{p.provider ?? '—'}</td>
                        <td className="mono small">{p.provider_reference ?? '—'}</td>
                        <td className="num small muted">{p.completed_at ? when(p.completed_at) : '—'}</td>
                      </tr>
                      {p.failure_reason && (
                        <tr>
                          <td />
                          <td colSpan={5} className="small" style={{ color: 'var(--danger)', paddingTop: 0 }}>
                            Why it failed: {p.failure_reason}
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>

        <div className="stack">
          <Card title="Audit timeline">
            <div className="timeline">
              {data.timeline.map((t) => (
                <div key={t.id} className={`tl-item ${t.action.includes('REJECT') || t.action.includes('FAILED') ? 'bad' : t.action === 'enrolment.not_matched' ? 'warn' : 'done'}`}>
                  <div className="what">{prettyAction(t.action)}</div>
                  <div className="when">{when(t.at)} · {t.actor}</div>
                  {t.reason && <div className="why">{t.reason}</div>}
                </div>
              ))}
            </div>
          </Card>

          {data.webhooks.length > 0 && (
            <Card title="Notified inDrive" flush>
              <div style={{ padding: '4px 0' }}>
                {data.webhooks.map((w) => (
                  <div className="check-row" key={w.sequenceNo}>
                    <span className={`tick ${w.deliveryStatus === 'delivered' ? 'pass' : w.deliveryStatus === 'dead' ? 'fail' : 'na'}`}>
                      {w.deliveryStatus === 'delivered' ? '✓' : w.deliveryStatus === 'dead' ? '✕' : '·'}
                    </span>
                    <span className="name mono small">{w.eventType}</span>
                    <span className="small muted">
                      {w.deliveredAt ? when(w.deliveredAt) : `${w.deliveryStatus}${w.attempts ? ` · ${w.attempts} attempts` : ''}`}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {confirm && (
        <Modal
          title={isRetry ? 'Retry this payout?' : 'Release this payout?'}
          description={isRetry
            ? 'The last attempt failed and no money left the float. Read the reason under Payout attempts first — if it is about the account, retrying will fail the same way.'
            : "KYC and fraud checks have passed. Releasing moves real money out of inDrive's float."}
          onClose={() => setConfirm(false)}
          footer={
            <>
              <button className="btn" onClick={() => setConfirm(false)}>Cancel</button>
              <button className="btn primary" onClick={release}>{isRetry ? 'Retry' : 'Release'} {data.amount_display}</button>
            </>
          }
        >
          <dl className="dl">
            <dt>Amount</dt><dd style={{ fontWeight: 600 }}>{data.amount_display}</dd>
            <dt>To</dt><dd className="mono">{data.destination_masked}</dd>
            <dt>Channel</dt><dd>{data.payout_channel === 'bank' ? `Bank ${data.bank_code ?? ''}` : data.wallet_provider}</dd>
            <dt>Driver</dt><dd>{data.referring_driver_id}</dd>
          </dl>
          {(data.referrer_risk.shares_identity_with > 0 || data.referrer_risk.shares_account_with > 0) && (
            <Banner tone={data.referrer_risk.shares_identity_with > 0 ? 'danger' : 'warn'} title="Check this first">
              This driver is connected to {data.referrer_risk.links.length} other driver account
              {data.referrer_risk.links.length === 1 ? '' : 's'}
              {data.referrer_risk.shares_identity_with > 0 ? ' by shared identity' : ' by shared payout account'}.
              That connection may not have existed when this referral was scored.
            </Banner>
          )}
          <p className="small muted" style={{ margin: 0 }}>
            This action is recorded against your account in the audit log. A referral can only ever be paid once.
          </p>
        </Modal>
      )}
    </div>
  );
}

/**
 * Where the driver has got to.
 *
 * A referral sitting in KYC_PENDING is waiting on a person, not on the engine,
 * and the useful question is which step they stopped at — never opened the
 * link, opened but did not consent, consented but did not submit. Each has a
 * different remedy, and none of them is visible from the status alone.
 */
function DriverKycCard({ id, kyc, role, cancellable, indriveName, onChanged }: {
  id: string; kyc: import('../api').ReferralDetail['driver_kyc']; role: Role; cancellable: boolean; indriveName: string | null;
  onChanged: () => void;
}) {
  const [link, setLink] = useState<string | null>(null);
  const [rechecking, setRechecking] = useState(false);
  const [recheck, setRecheck] = useState<{ matched: boolean; message: string; canOverride: boolean } | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptReason, setAcceptReason] = useState('');
  const [acceptBusy, setAcceptBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingName, setCheckingName] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const step = !kyc.opened_at ? 'not opened'
    : !kyc.consented_at ? 'opened, not consented'
    : !kyc.identity_submitted_at ? 'consented, identity not submitted'
    : 'submitted';

  const hoursLeft = Math.max(0, Math.round((new Date(kyc.expires_at).getTime() - Date.now()) / 3_600_000));

  return (
    <Card
      title="Waiting on the driver"
      action={
        <div className="row" style={{ gap: 8 }}>
        <button
          className="btn sm"
          disabled={rechecking}
          onClick={async () => {
            setRechecking(true);
            setError(null);
            setRecheck(null);
            try {
              const r = await api.recheckEnrolment(id);
              setRecheck(r);
              onChanged();
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setRechecking(false);
            }
          }}
        >
          {rechecking ? <span className="spin" /> : 'Re-check join-link details'}
        </button>
        {kyc.can_accept_join_link && role === 'zeeh_admin' && (
          <button className="btn sm" onClick={() => setCheckingName(true)}>Check name at NIBSS…</button>
        )}
        {kyc.can_accept_join_link && role === 'zeeh_admin' && (
          <button className="btn sm" onClick={() => setAccepting(true)}>Accept anyway…</button>
        )}
        <button
          className="btn sm"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              setLink((await api.kycLink(id)).kyc_url);
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? <span className="spin" /> : link ? 'New link' : 'Get link'}
        </button>
        {cancellable && role === 'zeeh_admin' && (
          <button className="btn sm danger" onClick={() => setCancelling(true)}>Cancel referral…</button>
        )}
        </div>
      }
    >
      {error && <Banner tone="danger">{error}</Banner>}
      {recheck && <Banner tone={recheck.matched ? 'ok' : 'warn'} title={recheck.matched ? 'Matched' : 'Not matched'}>{recheck.message}</Banner>}
      <dl className="dl">
        <dt>Progress</dt><dd>{step}</dd>
        <dt>Link issued</dt><dd>{kyc.link_issued_at ? when(kyc.link_issued_at) : <span className="muted">not yet</span>}</dd>
        <dt>Opened</dt><dd>{kyc.opened_at ? when(kyc.opened_at) : <span className="muted">no</span>}</dd>
        <dt>Consented</dt>
        <dd>{kyc.consented_at ? `${when(kyc.consented_at)} (${kyc.consent_version})` : <span className="muted">no</span>}</dd>
        <dt>Window closes</dt>
        <dd style={hoursLeft < 6 ? { color: 'var(--warn)', fontWeight: 600 } : undefined}>
          {when(kyc.expires_at)} · {hoursLeft}h left
        </dd>
      </dl>

      {link && (
        <div style={{ marginTop: 14 }}>
          <div className="field">
            <label>Send this to the driver</label>
            <input type="text" readOnly value={link} onFocus={(e) => e.currentTarget.select()} className="mono" />
            <span className="hint">
              Expires with the KYC window — re-issuing does not extend it. Treat it as a credential:
              anyone with this link can complete this driver's verification.
            </span>
          </div>
          <button
            className="btn sm"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(link);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              } catch {
                setError('Could not copy — select the link above and copy it manually.');
              }
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </div>
      )}
      {checkingName && <NameCheckModal id={id} onFile={indriveName} onClose={() => setCheckingName(false)} />}
      {cancelling && (
        <CancelModal
          id={id}
          onClose={() => setCancelling(false)}
          onCancelled={() => { setCancelling(false); onChanged(); }}
        />
      )}
      {accepting && (
        <Modal
          title="Accept these details anyway?"
          description="NIBSS has this driver's BVN registered to a different phone number than the one inDrive gave us."
          onClose={() => { setAccepting(false); setAcceptReason(''); }}
          footer={
            <>
              <button className="btn" onClick={() => { setAccepting(false); setAcceptReason(''); }}>Cancel</button>
              <button
                className="btn danger"
                disabled={acceptBusy || acceptReason.trim().length < 10}
                onClick={async () => {
                  setAcceptBusy(true);
                  setError(null);
                  try {
                    await api.acceptEnrolment(id, acceptReason.trim());
                    setAccepting(false);
                    setAcceptReason('');
                    onChanged();
                  } catch (e) {
                    setAccepting(false);
                    setError((e as Error).message);
                  } finally {
                    setAcceptBusy(false);
                  }
                }}
              >
                {acceptBusy ? <span className="spin" /> : 'Accept and record'}
              </button>
            </>
          }
        >
          <p className="small" style={{ marginTop: 0, lineHeight: 1.6 }}>
            You are vouching that this sign-up belongs to the inDrive driver on this referral — for example, inDrive
            confirmed the name matches. Only the phone check is waived. The BVN is still checked at NIBSS, and KYC still
            checks the account holder's name against it, plus sanctions and fraud. A reviewer will see the phone mismatch.
            Nothing is paid by this.
          </p>
          <div className="field">
            <label htmlFor="accept-reason">Why are you accepting it?</label>
            <textarea
              id="accept-reason"
              value={acceptReason}
              onChange={(e) => setAcceptReason(e.target.value)}
              placeholder="e.g. Eric Bonsu confirmed by email on 14 Oct that this driver's name matches the BVN."
            />
            <span className="hint">At least 10 characters. Your name and this reason are written to the audit log permanently.</span>
          </div>
        </Modal>
      )}
    </Card>
  );
}

const VERDICT: Record<'same' | 'likely' | 'different', { tone: string; label: string }> = {
  same: { tone: 'ok', label: 'Same name as inDrive' },
  likely: { tone: 'warn', label: 'Close — check it yourself' },
  different: { tone: 'danger', label: 'Different name' },
};

/**
 * The name on the BVN behind a phone-differs sign-up, from NIBSS.
 *
 * The phone failed, so the question left is whether the person who joined is
 * the driver inDrive paid us to reward. The name inDrive gave is that answer's
 * other half: filled in from their sheet when we have it, typed only when we
 * do not, and compared on the server. The result informs "Accept anyway…";
 * this changes nothing by itself.
 */
function NameCheckModal({ id, onFile, onClose }: { id: string; onFile: string | null; onClose: () => void }) {
  const [indriveName, setIndriveName] = useState(onFile ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signups, setSignups] = useState<SignupInspection[] | null>(null);

  return (
    <Modal
      title="Check name at NIBSS"
      description="See the name on the BVN this driver signed up with, and whether it matches the name inDrive has for them."
      onClose={onClose}
      footer={
        signups ? (
          <button className="btn" onClick={onClose}>Done</button>
        ) : (
          <>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button
              className="btn primary"
              disabled={busy || reason.trim().length < 10}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  setSignups((await api.inspectSignups(id, reason.trim(), indriveName.trim())).signups);
                } catch (e) {
                  setError((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? <span className="spin" /> : 'Look up at NIBSS'}
            </button>
          </>
        )
      }
    >
      {error && <Banner tone="danger">{error}</Banner>}
      {!signups && (
        <>
          <div className="field">
            <label htmlFor="indrive-name">Name inDrive has for this driver</label>
            <input
              id="indrive-name"
              type="text"
              value={indriveName}
              onChange={(e) => setIndriveName(e.target.value)}
              placeholder="First and last name, from inDrive's sheet"
            />
            <span className="hint">
              {!onFile ? "Not on file, so type it from inDrive's sheet. Used for this comparison only; it is not saved."
                : indriveName.trim() === onFile ? "Filled in from inDrive's sheet."
                : "Changed from the name on file, for this comparison only. The name on file stays as it is."}
            </span>
          </div>
          <div className="field">
            <label htmlFor="inspect-reason">Why are you looking?</label>
            <textarea
              id="inspect-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Phone differs — checking the BVN name against inDrive's list before accepting."
            />
            <span className="hint">At least 10 characters. Your name and this reason go on the audit log; the BVN holder's name does not.</span>
          </div>
        </>
      )}
      {signups && signups.length === 0 && (
        <p className="small" style={{ marginTop: 0 }}>No sign-up was found for this driver. Send them their claim link instead.</p>
      )}
      {signups && signups.map((s) => (
        <div key={s.bvnTail} style={{ borderTop: '1px solid var(--rule)', paddingTop: 12, marginTop: 12 }}>
          <dl className="dl">
            <dt>BVN</dt><dd className="mono">ending {s.bvnTail}</dd>
            <dt>Name at NIBSS</dt>
            <dd>
              {s.status === 'found' ? s.name ?? <span className="muted">none returned</span>
                : <span className="muted">NIBSS could not find this BVN. The driver probably mistyped it; send them their claim link.</span>}
            </dd>
            {s.nameVerdict && (
              <>
                <dt>Against inDrive</dt>
                <dd><span className={`pill ${VERDICT[s.nameVerdict].tone}`}>{VERDICT[s.nameVerdict].label}</span></dd>
              </>
            )}
            <dt>Phones on this BVN</dt>
            <dd className="mono">{s.phones.length ? s.phones.join(', ') : <span className="muted">none returned</span>}</dd>
          </dl>
        </div>
      ))}
      {signups && signups.some((s) => s.nameVerdict === 'same' || s.nameVerdict === 'likely') && (
        <p className="small" style={{ lineHeight: 1.6 }}>
          If you are satisfied it is the same person, close this and use <b>Accept anyway…</b> with a reason such as
          “NIBSS name matches inDrive's name for this driver, checked {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}.”
        </p>
      )}
      {signups && signups.length > 0 && signups.every((s) => s.nameVerdict === 'different') && (
        <p className="small" style={{ lineHeight: 1.6, color: 'var(--danger)' }}>
          The name does not match. Do not accept this sign-up; ask inDrive about this driver.
        </p>
      )}
    </Modal>
  );
}

/**
 * Close a waiting referral that must never be paid. Irreversible, so it asks
 * for a reason and says plainly what happens.
 */
function CancelModal({ id, onClose, onCancelled }: { id: string; onClose: () => void; onCancelled: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal
      title="Cancel this referral?"
      description="For a referral that must never be paid: test data, a duplicate, or one inDrive withdrew."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Keep it</button>
          <button
            className="btn danger"
            disabled={busy || reason.trim().length < 10}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.cancelReferral(id, reason.trim());
                onCancelled();
              } catch (e) {
                setError((e as Error).message);
                setBusy(false);
              }
            }}
          >
            {busy ? <span className="spin" /> : 'Cancel referral'}
          </button>
        </>
      }
    >
      {error && <Banner tone="danger">{error}</Banner>}
      <p className="small" style={{ marginTop: 0, lineHeight: 1.6 }}>
        It closes as rejected and cannot be reopened. The driver's claim link stops working, and inDrive is told it was
        rejected. If a real driver should still be paid, do not cancel. Use their claim link, or <b>Accept anyway…</b>.
      </p>
      <div className="field">
        <label htmlFor="cancel-reason">Why are you cancelling it?</label>
        <textarea
          id="cancel-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Test referral from 13 Oct (TEST-A to TEST-B); never a real driver."
        />
        <span className="hint">At least 10 characters. Your name and this reason are written to the audit log permanently.</span>
      </div>
    </Modal>
  );
}

/** A null hit means "not screened", not "passed". */
const invert = (hit: boolean | null): boolean | null => (hit === null ? null : !hit);

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '11px 18px 7px', fontSize: 10.5, textTransform: 'uppercase',
      letterSpacing: '0.08em', color: 'var(--ink-3)', fontWeight: 600,
      borderBottom: '1px solid var(--rule)', background: 'var(--surface-sunk)',
    }}>
      {children}
    </div>
  );
}

function Check({ name, state, note }: { name: string; state: boolean | null; note?: string }) {
  return (
    <div className="check-row">
      <span className={`tick ${state === null ? 'na' : state ? 'pass' : 'fail'}`}>
        {state === null ? '·' : state ? '✓' : '✕'}
      </span>
      <span className="name">{name}</span>
      {note && <span className="small muted">{note}</span>}
    </div>
  );
}

/** Acronyms that must not be sentence-cased into "Kyc" or "Aml". */
const ACRONYMS = new Set(['kyc', 'aml', 'bvn', 'nin', 'pep']);

function prettyAction(action: string): string {
  if (action === 'enrolment.matched') return 'Join-link details used';
  if (action === 'enrolment.not_matched') return 'Join-link details not used';
  const raw = action.startsWith('status.') ? action.slice(7) : action;
  const words = raw.replace(/[._]/g, ' ').toLowerCase().split(' ').filter(Boolean);
  return words
    .map((w, i) => (ACRONYMS.has(w) ? w.toUpperCase() : i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}
